import React, { useState, useEffect, useRef } from 'react';
import { 
  Users,
  Search,
  Plus,
  Mail,
  Phone,
  Building,
  Tag,
  Star,
  Edit2,
  Trash2,
  Filter,
  Download,
  Upload,
  Send,
  Calendar,
  AlertCircle,
  Globe,
  Linkedin,
  Instagram
} from 'lucide-react';
import { api } from '../utils/api';
import ContactModal from '../components/ContactModal';
import Modal from '../components/Modal';
import { exportToCSV } from '../utils/export';

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deletingContact, setDeletingContact] = useState(null);
  const [importingContacts, setImportingContacts] = useState(false);
  const fileInputRef = useRef(null);

  const tags = ['all', 'team', 'venue', 'partner', 'artist', 'supplier', 'media', 'priority', 'investor', 'vendor', 'agency', 'government', 'sponsor'];

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.contacts.getAll();
      setContacts(response.contacts);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (contactData) => {
    try {
      const response = await api.contacts.create(contactData);
      setContacts([...contacts, response.contact]);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding contact:', error);
      throw error;
    }
  };

  const handleEditContact = async (contactData) => {
    try {
      const response = await api.contacts.update(editingContact.id, contactData);
      setContacts(contacts.map(c => c.id === editingContact.id ? response.contact : c));
      setEditingContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  };

  const handleDeleteContact = async () => {
    try {
      await api.contacts.delete(deletingContact.id);
      setContacts(contacts.filter(c => c.id !== deletingContact.id));
      setDeletingContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Failed to delete contact');
    }
  };

  const handleImportContacts = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportingContacts(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const rows = text.split('\n');
          const headers = rows[0].split(',').map(h => h.trim());
          
          const contactsToImport = [];
          
          for (let i = 1; i < rows.length; i++) {
            if (!rows[i].trim()) continue;
            
            const values = rows[i].split(',').map(v => v.trim());
            const contact = {};
            
            headers.forEach((header, index) => {
              if (header.toLowerCase() === 'tags') {
                contact.tags = values[index] ? values[index].split(';').map(t => t.trim()) : [];
              } else {
                contact[header.toLowerCase()] = values[index] || '';
              }
            });
            
            if (contact.name) {
              contactsToImport.push(contact);
            }
          }
          
          if (contactsToImport.length > 0) {
            const response = await api.contacts.import(contactsToImport);
            setContacts([...contacts, ...response.contacts]);
            setError(null);
            alert(`Successfully imported ${response.imported} contacts`);
          } else {
            setError('No valid contacts found in CSV');
          }
        } catch (error) {
          console.error('Error parsing CSV:', error);
          setError('Failed to parse CSV file');
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing contacts:', error);
      setError('Failed to import contacts');
    } finally {
      setImportingContacts(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportContacts = () => {
    const exportData = filteredContacts.map(contact => ({
      Name: contact.name,
      Role: contact.role || '',
      Company: contact.company || '',
      Email: contact.email || '',
      Phone: contact.phone || '',
      Website: contact.website || '',
      LinkedIn: contact.linkedin || '',
      Instagram: contact.instagram || '',
      Tags: contact.tags.join('; '),
      Notes: contact.notes || '',
      'Last Contact': contact.lastContact ? new Date(contact.lastContact).toLocaleDateString() : ''
    }));
    
    exportToCSV(exportData, 'contacts.csv');
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = filterTag === 'all' || contact.tags?.includes(filterTag);
    return matchesSearch && matchesTag;
  });

  const getTagColor = (tag) => {
    const colors = {
      team: 'bg-[#2D6B42]/20 text-[#2D6B42]',
      venue: 'bg-[#D4A574]/20 text-[#D4A574]',
      partner: 'bg-blue-500/20 text-blue-500',
      artist: 'bg-purple-500/20 text-purple-500',
      supplier: 'bg-orange-500/20 text-orange-500',
      media: 'bg-pink-500/20 text-pink-500',
      priority: 'bg-red-500/20 text-red-500',
      founder: 'bg-[#888888]/20 text-[#888888]',
      investor: 'bg-green-500/20 text-green-500',
      vendor: 'bg-yellow-500/20 text-yellow-500',
      agency: 'bg-indigo-500/20 text-indigo-500',
      government: 'bg-gray-500/20 text-gray-500',
      sponsor: 'bg-cyan-500/20 text-cyan-500'
    };
    return colors[tag] || 'bg-[#333333]/20 text-[#888888]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Contacts</h1>
          <p className="text-[#888888]">Manage your network and relationships</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Add Contact
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-[#888888]" size={20} />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark w-full pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="text-[#888888]" size={20} />
          <select
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="input-dark"
          >
            {tags.map(tag => (
              <option key={tag} value={tag}>
                {tag === 'all' ? 'All Tags' : tag.charAt(0).toUpperCase() + tag.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{filteredContacts.length}</p>
          <p className="text-sm text-[#888888]">Total Contacts</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">
            {contacts.filter(c => c.tags?.includes('venue')).length}
          </p>
          <p className="text-sm text-[#888888]">Venues</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">
            {contacts.filter(c => c.tags?.includes('partner')).length}
          </p>
          <p className="text-sm text-[#888888]">Partners</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">
            {contacts.filter(c => c.tags?.includes('priority')).length}
          </p>
          <p className="text-sm text-[#888888]">Priority</p>
        </div>
      </div>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContacts.map((contact) => (
          <div key={contact.id} className="card-dark hover:border-[#2D6B42] transition-all duration-300">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-[#0A0A0A] rounded-full flex items-center justify-center">
                  <span className="text-lg font-medium">
                    {contact.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
                <div>
                  <h3 className="font-medium">{contact.name}</h3>
                  <p className="text-sm text-[#888888]">{contact.role}</p>
                </div>
              </div>
              {contact.tags?.includes('priority') && (
                <Star className="text-[#D4A574]" size={16} fill="currentColor" />
              )}
            </div>

            {contact.company && (
              <div className="flex items-center gap-2 text-sm mb-2">
                <Building size={16} className="text-[#888888]" />
                <span>{contact.company}</span>
              </div>
            )}

            {contact.email && (
              <div className="flex items-center gap-2 text-sm mb-2">
                <Mail size={16} className="text-[#888888]" />
                <a href={`mailto:${contact.email}`} className="hover:text-[#D4A574] truncate">
                  {contact.email}
                </a>
              </div>
            )}

            {contact.phone && (
              <div className="flex items-center gap-2 text-sm mb-2">
                <Phone size={16} className="text-[#888888]" />
                <a href={`tel:${contact.phone}`} className="hover:text-[#D4A574]">
                  {contact.phone}
                </a>
              </div>
            )}

            <div className="flex gap-2 mb-4">
              {contact.website && (
                <a 
                  href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-[#0A0A0A] rounded transition-colors"
                  title="Website"
                >
                  <Globe size={16} className="text-[#888888]" />
                </a>
              )}
              {contact.linkedin && (
                <a 
                  href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-[#0A0A0A] rounded transition-colors"
                  title="LinkedIn"
                >
                  <Linkedin size={16} className="text-[#888888]" />
                </a>
              )}
              {contact.instagram && (
                <a 
                  href={`https://instagram.com/${contact.instagram.replace('@', '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-[#0A0A0A] rounded transition-colors"
                  title="Instagram"
                >
                  <Instagram size={16} className="text-[#888888]" />
                </a>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {contact.tags?.map((tag, index) => (
                <span 
                  key={index} 
                  className={`text-xs px-2 py-1 rounded-full ${getTagColor(tag)}`}
                >
                  {tag}
                </span>
              ))}
            </div>

            {contact.notes && (
              <p className="text-xs text-[#888888] mb-4 line-clamp-2">{contact.notes}</p>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-[#333333]">
              <span className="text-xs text-[#888888]">
                {contact.lastContact ? `Last contact: ${new Date(contact.lastContact).toLocaleDateString()}` : 'No contact yet'}
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setEditingContact(contact)}
                  className="p-1 hover:bg-[#0A0A0A] rounded transition-colors"
                  title="Edit"
                >
                  <Edit2 size={14} />
                </button>
                <button 
                  onClick={() => setDeletingContact(contact)}
                  className="p-1 hover:bg-[#0A0A0A] rounded transition-colors text-red-500"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredContacts.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Users className="mx-auto text-[#333333] mb-4" size={48} />
            <p className="text-[#888888]">No contacts found</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card-dark">
        <h2 className="text-xl font-light mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
            disabled={importingContacts}
          >
            <Upload size={16} />
            {importingContacts ? 'Importing...' : 'Import CSV'}
          </button>
          <button 
            onClick={handleExportContacts}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
          >
            <Download size={16} />
            Export CSV
          </button>
          <button className="btn-secondary text-sm flex items-center justify-center gap-2">
            <Send size={16} />
            Email Campaign
          </button>
          <button className="btn-secondary text-sm flex items-center justify-center gap-2">
            <Calendar size={16} />
            Sync Calendar
          </button>
        </div>
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleImportContacts}
        className="hidden"
      />

      {/* Add/Edit Contact Modal */}
      <ContactModal
        isOpen={showAddModal || !!editingContact}
        onClose={() => {
          setShowAddModal(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        onSave={editingContact ? handleEditContact : handleAddContact}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingContact}
        onClose={() => setDeletingContact(null)}
        title="Delete Contact"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete <strong>{deletingContact?.name}</strong>?</p>
          <p className="text-sm text-[#888888]">This action cannot be undone.</p>
          
          <div className="flex gap-3 justify-end pt-4">
            <button
              onClick={() => setDeletingContact(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteContact}
              className="btn-primary bg-red-500 hover:bg-red-600"
            >
              Delete Contact
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContactsPage;